import { useLayoutEffect, useState, type RefObject } from "react"

import { formatHoverTime, HOVER_TIME_LABEL_CLASS } from "./hoverTimeLabel"
import { measureAudioTimeline, timelineLineStyle } from "./measureAudioTimeline"

/** How close to the right edge a hover label flips to the line's left side. */
const HOVER_LABEL_FLIP_PIXELS = 90

/**
 * A line down the whole timeline at a moment on the recording.
 *
 * `"playhead"` is the playback position. `"hover"` is a moment the pointer is
 * over somewhere else on the same clock, drawn the way the player's own hover
 * preview is, label included. A hover outside the recording is not drawn,
 * because pinning it to an edge would claim a moment the recording never had.
 */
export function AudioTimelineCursor({
  root,
  enabled,
  seconds,
  layoutKey,
  variant = "playhead",
}: {
  root: RefObject<HTMLDivElement | null>
  enabled: boolean
  seconds: number
  layoutKey: string
  variant?: "playhead" | "hover"
}) {
  const [timeline, setTimeline] = useState<ReturnType<
    typeof measureAudioTimeline
  > | null>(null)
  useLayoutEffect(() => {
    const container = root.current
    if (!enabled || !container) {
      return
    }
    const refresh = () => setTimeline(measureAudioTimeline(container))
    refresh()
    const observer = new ResizeObserver(refresh)
    observer.observe(container)
    container
      .querySelectorAll<HTMLElement>(
        "[data-audio-timeline-duration], [data-audio-timeline-viewport]",
      )
      .forEach((element) => observer.observe(element))
    container.addEventListener("scroll", refresh, true)
    return () => {
      observer.disconnect()
      container.removeEventListener("scroll", refresh, true)
    }
  }, [root, enabled, layoutKey])
  const track = timeline?.tracks[0]
  if (!enabled || !timeline || !track || !Number.isFinite(seconds)) {
    return null
  }
  if (variant === "hover" && (seconds < 0 || seconds > track.duration)) {
    return null
  }
  const fraction = Math.max(0, Math.min(1, seconds / track.duration))
  const left =
    track.rect.left +
    fraction * track.rect.width -
    timeline.containerRect.left -
    timeline.clientLeft
  const top = timeline.top - timeline.containerRect.top - timeline.clientTop
  const height = timeline.bottom - timeline.top
  if (variant === "hover") {
    return (
      <div
        aria-hidden="true"
        data-testid="audio-timeline-external-hover"
        className="pointer-events-none absolute z-20 border-l border-coval-text-muted"
        style={timelineLineStyle(left, top, height)}
      >
        <span
          className={`absolute top-0 ${HOVER_TIME_LABEL_CLASS}`}
          style={{
            transform:
              track.rect.right -
                (track.rect.left + fraction * track.rect.width) <
              HOVER_LABEL_FLIP_PIXELS
                ? "translateX(-100%)"
                : undefined,
          }}
        >
          {formatHoverTime(seconds)}
        </span>
      </div>
    )
  }
  return (
    <div
      aria-hidden="true"
      data-testid="audio-timeline-cursor"
      className="pointer-events-none absolute z-30 w-0.5 bg-coval-text"
      style={timelineLineStyle(left, top, height)}
    />
  )
}
